import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getAccommodationsBlurPlaceholder } from "@/utils/accommodationsBlurPlaceholders";
import { Room } from "@/types/room";
import {
  Users,
  Square,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Wifi,
  Tv,
  Wind,
  ChefHat,
  Shield,
  Star,
  Share2,
  Coffee,
  Home,
  Hotel,
  Sofa,
  DoorOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import FloatingIconStack from "@/components/FloatingIconStack";
import { useLanguage } from "@/contexts/LanguageContext";
import { useViewportAutoplay } from "@/hooks/useViewportAutoplay";

interface FamilySuiteSectionProps {
  room: Room;
  onViewDetails?: (roomId: string) => void;
  onQuickBook?: (roomId: string) => void;
}

const FamilySuiteSection: React.FC<FamilySuiteSectionProps> = ({
  room,
  onViewDetails,
  onQuickBook,
}) => {
  const { t } = useLanguage();

  // Viewport-based auto-play hook
  const {
    currentImageIndex,
    isAutoPlaying,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick,
  } = useViewportAutoplay({
    imageCount: room.images.length,
    intervalMs: 4000,
    threshold: 0.7,
  });

  const handleViewDetails = () => {
    onViewDetails?.(room.id);
  };

  const handleQuickBook = () => {
    onQuickBook?.(room.id);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/accommodations#family-suite`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} - Park Hotel Beatenberg`,
          text: `Check out our ${room.name} with panoramic views and family amenities`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard if share is cancelled
        copyToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const currentImage = room.images[currentImageIndex];

  // Feature badges data
  const featureBadges = [
    {
      icon: Users,
      label: "Occupancy",
      value: t("rooms.familySuiteBadges.occupancy"),
      description: `${room.capacity.adults} adults, ${room.capacity.children} children`,
    },
    {
      icon: Square,
      label: "Room Size",
      value: t("rooms.familySuiteBadges.roomSize"),
      description: `${room.size.squareFeet} sq ft`,
    },
    {
      icon: Bed,
      label: "Bedding",
      value: t("rooms.familySuiteBadges.bedding"),
      description: "Flexible configuration",
    },
    {
      icon: Bath,
      label: "Bathrooms",
      value: t("rooms.familySuiteBadges.bathrooms"),
      description: "Ensuite + shared",
    },
  ];

  return (
    <section
      id="family-suite"
      className="py-16 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden"
      data-oid="d:yhsj4"
    >
      {/* Background Quadrilaterals */}
      <div
        className="absolute top-0 right-0 w-96 h-96 transform rotate-45 translate-x-48 -translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="6x-vh9v"
      ></div>
      <div
        className="absolute bottom-0 left-0 w-96 h-96 transform rotate-45 -translate-x-48 translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="qkbgapo"
      ></div>
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        data-oid="yy9ij-p"
      >
        {/* Mobile Header - Shown only on mobile */}
        <div className="mb-8 lg:hidden" data-oid="44tt-qk">
          <div className="mb-4" data-oid="unoxcf3">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-playfair text-gray-900"
              data-oid="l0uyhr0"
            >
              {t("rooms.familySuite")}
            </h2>
          </div>

          {/* Key badges below title - Mobile */}
          <div
            className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap"
            data-oid="8de.x13"
          >
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="03:n2:1"
            >
              <Users
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="pviknzw"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="v80nu_:"
              >
                {featureBadges[0].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="9icv-7i"
            >
              <Square
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="1y9gi6q"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="fcojj_i"
              >
                {featureBadges[1].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="a-8-yw."
            >
              <Hotel
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="o-i16ve"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="_wuemxd"
              >
                3 Bedrooms
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="5b_37t4"
            >
              <Sofa
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="joh9vs_"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="_2enp21"
              >
                {t("rooms.familySuiteFeatures.hallLivingRoom")}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout: Carousel Left (60%), Content Right (40%) */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[50%_50%] xl:grid-cols-[55%_45%] 2xl:grid-cols-[60%_40%] gap-6 lg:gap-8 xl:gap-12 mb-12"
          data-oid="7i3nh52"
        >
          {/* Left Column - Image Carousel */}
          <div
            ref={elementRef}
            className="relative w-full aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden rounded-2xl shadow-2xl"
            data-oid=".7wvbxq"
          >
            <div
              className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              data-oid="piex-2t"
            >
              {room.images.map((image, index) => (
                <div
                  key={image.id}
                  className="w-full h-full flex-shrink-0"
                  data-oid="l_2gu3i"
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    priority={index === 0}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    rootMargin={index === 0 ? undefined : "200px"}
                    blurDataURL={getAccommodationsBlurPlaceholder(image.url)}
                    responsive={true}
                    data-oid="k0o7c4d"
                  />
                </div>
              ))}
            </div>

            {/* Image Navigation */}
            {room.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  data-oid="l7_lk4g"
                >
                  <ChevronLeft className="w-5 h-5" data-oid="mgy7zs:" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  data-oid="7f6nbro"
                >
                  <ChevronRight className="w-5 h-5" data-oid="gynme5p" />
                </Button>
              </>
            )}

            {/* Image Indicators */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
                data-oid="mz7ht21"
              >
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    onClick={() => handleImageClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    data-oid="9eejr9x"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Content */}
          <div
            className="flex flex-col gap-8 min-w-0 overflow-hidden pr-4 lg:pr-6 xl:pr-8"
            data-oid="s:q.-z0"
          >
            {/* Desktop Header - Shown only on desktop */}
            <div className="hidden lg:block" data-oid="4_:oc25">
              <div className="mb-4" data-oid=":2_0z57">
                <h2
                  className="text-3xl xl:text-4xl 2xl:text-5xl font-playfair text-gray-900"
                  data-oid="818_z2_"
                >
                  {t("rooms.familySuite")}
                </h2>
              </div>

              {/* Key badges below title - Desktop */}
              <div
                className="flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-wrap"
                data-oid="obikldi"
              >
                <div className="flex items-center gap-2" data-oid="pkr2gat">
                  <Users
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="u7xzfhv"
                  />

                  <span className="text-sm text-gray-900" data-oid="y3rlaff">
                    {featureBadges[0].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="-qwsuul">
                  <Square
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="zio4tvr"
                  />

                  <span className="text-sm text-gray-900" data-oid="-vi_vr1">
                    {featureBadges[1].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="x-84:i-">
                  <Hotel
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="8.sb5rn"
                  />

                  <span className="text-sm text-gray-900" data-oid="r33q.zo">
                    3 Bedrooms
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="l8n:zgh">
                  <Sofa
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="xap2_3j"
                  />

                  <span className="text-sm text-gray-900" data-oid="u6mtzt3">
                    {t("rooms.familySuiteFeatures.hallLivingRoom")}
                  </span>
                </div>
              </div>
            </div>

            {/* 1. Description */}
            <div className="overflow-hidden" data-oid="wnm041g">
              <p
                className="text-sm lg:text-base xl:text-lg text-gray-700 leading-relaxed break-words"
                data-oid="fpg_9ij"
              >
                <span className="sm:hidden" data-oid="1wyph88">
                  {t("rooms.familySuiteDescription.mobile")}
                </span>
                <span className="hidden sm:inline" data-oid="gu4shg7">
                  {t("rooms.familySuiteDescription.desktop")}
                </span>
              </p>
            </div>

            {/* 2. All Features & Amenities - Consolidated */}
            <div className="grid grid-cols-1 gap-y-4" data-oid="ibpjo.z">
              {/* Bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid=".q.ihef"
              >
                <Bed
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="86akr32"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="atj4h80"
                >
                  {featureBadges[2].value}
                </span>
              </div>

              {/* Private Balcony */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="3g_xl0w"
              >
                <DoorOpen
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid=".nmv3_0"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="2m.r.0y"
                >
                  {t("rooms.familySuiteFeatures.privateBalcony")}
                </span>
              </div>

              {/* Free Wifi */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="x8l2g78"
              >
                <Wifi
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid=":dn1q.6"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="76i1hq6"
                >
                  Free Wifi
                </span>
              </div>

              {/* Flatscreen TV */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="jt8w5.f"
              >
                <Tv
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="ymiswc3"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="erns-v_"
                >
                  Flatscreen TV
                </span>
              </div>

              {/* Alpine views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="9w9__j7"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid=".3__8ei"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="29b.juh"
                >
                  Alpine views
                </span>
              </div>

              {/* Rustic pine interiors */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="sbojbr."
              >
                <Home
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="_llg-n0"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="kvrcqt4"
                >
                  Rustic pine interiors
                </span>
              </div>

              {/* Ensuite coffee machine */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="146g0n_"
              >
                <Coffee
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="a73:9y5"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="yt3.bis"
                >
                  Ensuite coffee machine
                </span>
              </div>

              {/* Premium organic bio cotton bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="lbu00ji"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="w40s2z1"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="y-k5_b:"
                >
                  Premium organic bio cotton bedding
                </span>
              </div>
            </div>

            {/* 3. Book Now and Share Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              data-oid="8-i..:v"
            >
              <Button
                asChild
                size="lg"
                className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Family Suite Section"
                data-oid="f2u28:w"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  data-oid="bp96i5i"
                >
                  {t("nav.bookNow")}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-white/80 hover:bg-alpine-gold hover:text-white border-gray-300 hover:border-alpine-gold transition-all duration-300 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105"
                onClick={handleShare}
                aria-label="Share Family Suite"
                data-oid="w:qzcur"
              >
                <Share2 className="w-4 h-4 mr-2" data-oid="us2ipkh" />
                {t("common.share")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Icon Stack for Room Features - Hidden for now */}
      {/* <FloatingIconStack position="right" /> */}
    </section>
  );
};

export default FamilySuiteSection;
